import {createAsyncThunk} from "@reduxjs/toolkit";

import {fetchData, fetchDataNew, fetchList, fetchListNew} from "../../../../hook/axios.hook";
import {checkEmptyFields} from "../../../../utilities/checkEmptyFields";
import formSearchParams from "../../../../utilities/formSearchParams";

//PATH
const FACTORIES_PATH = "factories";
const FACT_COND_PATH = "factories/conditions";
const CONTACTS_PATH = "contacts";
const FACT_COND_TEMPL_PATH = "factories/conditions/emailTemplates";
const FACT_CONTROL_TERMS_PATH = "factories/conditions/controlTerms";

//*** FACTORIES ***
//get
export const fetchAllFactories = createAsyncThunk("factories/getAllFactories", async params =>
  fetchList(`${FACTORIES_PATH}/getAll`, "get", {params: checkEmptyFields(params)}),
);
export const fetchFactoryGroups = createAsyncThunk("factories/getFactoryGroups", async factoryId =>
  fetchList(`${FACTORIES_PATH}/getGroups/${factoryId}`, "get"),
);
export const getFactoryFilter = createAsyncThunk("factories/getFactoryFilter", async params =>
  fetchList(`${FACTORIES_PATH}/getFilters`, "get", {params: checkEmptyFields(params)}),
);
export const fetchReferences = createAsyncThunk("factories/getReferences", async () =>
  fetchData(`${FACTORIES_PATH}/getReferences`, "get"),
);
export const fetchFactory = createAsyncThunk("factories/getFactoryById", async (factoryId, {rejectWithValue}) =>
  fetchListNew(`${FACTORIES_PATH}/get/${factoryId}`, "get", {rejectWithValue}),
);
//post
export const addFactory = createAsyncThunk("factories/addFactory", async data =>
  fetchData(`${FACTORIES_PATH}/add`, "post", data),
);
export const addFactoryNote = createAsyncThunk("factories/addNote", async ({factoryId, data}) => {
  const resp = await fetchData(`${FACTORIES_PATH}/notes/add/${factoryId}`, "post", data);
  const note = {
    noteId: resp.noteId,
    note: resp.note,
  };
  return note;
});
//patch
export const saveFactory = createAsyncThunk("factories/saveFactory", async factoryId =>
  fetchData(`${FACTORIES_PATH}/save/${factoryId}`, "patch"),
);
export const updateFactory = createAsyncThunk("factories/updateFactory", async ({factoryId, data}) =>
  fetchData(`${FACTORIES_PATH}/update/${factoryId}`, "patch", data),
);
export const updateFactoryNote = createAsyncThunk("factories/updateNote", async ({noteId, data}) => {
  const resp = await fetchData(`${FACTORIES_PATH}/notes/${noteId}`, "patch", data);
  const note = {
    noteId: resp.noteId,
    note: resp.note,
  };
  return note;
});
//delete
export const deleteFactoryNote = createAsyncThunk("factories/deleteNote", async noteId =>
  fetchData(`${FACTORIES_PATH}/notes/${noteId}`, "delete").then(() => noteId),
);
export const cancelFactoryCreation = createAsyncThunk(
  "factories/cancelFactoryCreation",
  async (data, {rejectWithValue}) => {
    const factoryId = data?.factoryId;
    const force = data?.force;
    const withoutError = data?.withoutError;

    return fetchDataNew(`${FACTORIES_PATH}/cancel/${factoryId}`, "delete", {
      rejectWithValue,
      withoutError,
      params: force ? {force: true} : undefined,
    });
  },
);
export const cancelFactoryUpdate = createAsyncThunk("factories/cancelFactoryUpdate", async factoryId =>
  fetchData(`${FACTORIES_PATH}/cancelUpdate/${factoryId}`, "delete"),
);

export const factoryAppointManager = createAsyncThunk(
  "factories/appointManager",
  async ({factoryId, productGroupId, managerFeaId}) =>
    fetchData(`${FACTORIES_PATH}/appointManager/${factoryId}/${productGroupId}/${managerFeaId}`, "patch"),
);
export const factoryDisappointManager = createAsyncThunk(
  "factories/disappointManager",
  async ({factoryId, productGroupId}) =>
    fetchData(`${FACTORIES_PATH}/disappointManager/${factoryId}/${productGroupId}`, "patch"),
);
//**factories V2

export const getAllFactoriesV2 = createAsyncThunk(
  "factoriesV2/getAllFactories",
  async (params = {}, {rejectWithValue, signal}) =>
    fetchDataNew(`v2/${FACTORIES_PATH}`, "get", {params, rejectWithValue, signal}),
);
export const getFactoryByIdV2 = createAsyncThunk("factoriesV2/getFactoryById", async factoryId =>
  fetchDataNew(`v2/factories/${factoryId}`, "get"),
);
export const getFactoryFilters = createAsyncThunk(
  "factoriesV2/getFactoryFilters",
  async (params = {}, {rejectWithValue, signal}) =>
    fetchDataNew(`v2/${FACTORIES_PATH}/getFilters`, "get", {params, rejectWithValue, signal}),
);
export const updateFactoryV2 = createAsyncThunk("factoriesV2/updateFactory", async ({factoryId, ...body}) =>
  fetchDataNew(`v2/${FACTORIES_PATH}/${factoryId}`, "patch", {body}),
);
export const getContactByIdV2 = createAsyncThunk("factoriesV2/getContactById", async contactId =>
  fetchDataNew(`v2/contacts/${contactId}`, "get"),
);
export const createExternalAccountV2 = createAsyncThunk("factoriesV2/createAccount", async body => {
  return fetchData(`/finance/externalAccounts`, "post", body);
});
export const updateExternalAccountV2 = createAsyncThunk(
  "factoriesV2/updateAccount",
  async ({externalAccountId, ...body}) => {
    if (!externalAccountId) return;
    return fetchData(`/finance/externalAccounts/update/${externalAccountId}`, "patch", body);
  },
);
export const getContactByEmail = createAsyncThunk("factoriesV2/getEmail", async params =>
  fetchDataNew("v2/contacts/getByEmail", "get", {params}),
);
export const addContactPerson = createAsyncThunk("factoryV2/addContactPerson", async body =>
  fetchDataNew("v2/contacts", "post", {body}),
);
export const updateContactPerson = createAsyncThunk("factoryV2/updateContactPerson", async ({contactId, ...body}) =>
  fetchDataNew(`v2/contacts/${contactId}`, "patch", {body}),
);
export const addFactoryContactV2 = createAsyncThunk("factoryV2/addFactoryContact", async body =>
  fetchDataNew("v2/factories/contacts", "post", {body}),
);
export const updateFactoryContactV2 = createAsyncThunk(
  "factoryV2/updateFactoryContact",
  async ({factoryId, contactId, ...body}) =>
    fetchDataNew(`v2/factories/contacts/${factoryId}/${contactId}`, "patch", {body}),
);
export const getFactoryContactV2 = createAsyncThunk("factoryV2/getFactoryContact", async ({factoryId, contactId}) =>
  fetchDataNew(`v2/factories/contacts/${factoryId}/${contactId}`, "get"),
);
export const getFactoryContactsV2 = createAsyncThunk("factoryV2/getAllFactoryContacts", async params =>
  fetchDataNew("v2/factories/contacts", "get", {params}),
);

//*** SUPPLY CONDITIONS ***
//get
export const fetchAllConditions = createAsyncThunk("factories/fetchAllConditions", async paramsObject => {
  const searchParams = formSearchParams(paramsObject);
  return fetchList(`${FACT_COND_PATH}/getAll${searchParams}`, "get");
});
export const fetchFactoryConditions = createAsyncThunk("factories/fetchFactoryConditions", async factoryId => {
  return fetchList(`${FACT_COND_PATH}/getAll?factoryId=${factoryId}`, "get");
});
export const fetchFactoryContactConditions = createAsyncThunk(
  "factories/fetchFactoryContactConditions",
  async ({factoryId, contactId}) => {
    return fetchList(`${FACT_COND_PATH}/getAll?factoryId=${factoryId}&contactId=${contactId}`, "get");
  },
);
export const fetchSupplyCondition = createAsyncThunk("factories/fetchSupplyCondition", async conditionId =>
  fetchList(`${FACT_COND_PATH}/get/${conditionId}`, "get"),
);
export const fetchFilters = createAsyncThunk("factories/fetchFilters", async () =>
  fetchList(`${FACT_COND_PATH}/getFilters`, "get"),
);
//post
export const addCondition = createAsyncThunk("factories/addCondition", async ({factoryId, contactId, data}) =>
  fetchData(`${FACT_COND_PATH}/add/${factoryId}/${contactId}`, "post", data),
);
//patch
export const updateCondition = createAsyncThunk("factories/updateCondition", async ({conditionId, data}) =>
  fetchData(`${FACT_COND_PATH}/update/${conditionId}`, "patch", data),
);
//delete
export const deleteCondition = createAsyncThunk("factories/deleteCondition", async conditionId =>
  fetchData(`${FACT_COND_PATH}/delete/${conditionId}`, "delete"),
);

//*** REPRESENTATIVE CONTACTS ***
//get
export const fetchAllContacts = createAsyncThunk("factories/getAllContacts", async () =>
  fetchList(`${CONTACTS_PATH}/getAll`, "get"),
);
export const fetchContact = createAsyncThunk("factories/getContact", async contactId =>
  fetchList(`${CONTACTS_PATH}/get/${contactId}`, "get"),
);
//post
export const addContact = createAsyncThunk("factories/addContact", async (data, {rejectWithValue}) =>
  fetchDataNew(`${CONTACTS_PATH}/add`, "post", {rawBody: data, rejectWithValue}),
);
export const addContactNote = createAsyncThunk("factories/addContactNote", async ({contactId, data}) => {
  const res = await fetchData(`${CONTACTS_PATH}/notes/add/${contactId}`, "post", data);
  const contactNote = {
    noteId: res.noteId,
    note: res.note,
  };
  return contactNote;
});
//patch
export const updateContact = createAsyncThunk("factories/updateContact", async ({contactId, data}) =>
  fetchData(`${CONTACTS_PATH}/update/${contactId}`, "patch", data),
);
export const updateContactNote = createAsyncThunk("factories/updateContactNote", async ({noteId, data}) => {
  const res = await fetchData(`${CONTACTS_PATH}/notes/${noteId}`, "patch", data);
  const contactNote = {
    noteId: res.noteId,
    note: res.note,
  };
  return contactNote;
});
//delete
export const deleteContact = createAsyncThunk("factories/deleteContact", async contactId =>
  fetchData(`${CONTACTS_PATH}/delete/${contactId}`, "delete"),
);
export const deleteContactNote = createAsyncThunk("factories/deleteContactNote", async noteId =>
  fetchData(`${CONTACTS_PATH}/notes/${noteId}`, "delete").then(() => noteId),
);

//*** EMAIL TEMPLATES ***
export const fetchEmailTemplates = createAsyncThunk("factories/fetchEmailTemplates", async params =>
  fetchList(`${FACT_COND_TEMPL_PATH}/getAll`, "get", {params}),
);

export const addEmailTemplate = createAsyncThunk("factories/addEmailTemplate", async ({conditionId, values}) =>
  fetchData(`${FACT_COND_TEMPL_PATH}/add/${conditionId}`, "post", values),
);

export const updateEmailTemplate = createAsyncThunk("factories/updateEmailTemplate", async ({templateId, values}) =>
  fetchData(`${FACT_COND_TEMPL_PATH}/update/${templateId}`, "patch", values),
);

export const deleteEmailTemplate = createAsyncThunk("factories/deleteEmailTemplate", async templateId =>
  fetchData(`${FACT_COND_TEMPL_PATH}/delete/${templateId}`, "delete"),
);

//*** CONTROL TERMS ***
//get
export const fetchControlTerms = createAsyncThunk("factories/fetchControlTerms", async conditionId =>
  fetchList(`${FACT_CONTROL_TERMS_PATH}/getAll`, "get", {params: {conditionId}}),
);
//post
export const addControlTerms = createAsyncThunk("factories/addControlTerms", async conditionId =>
  fetchList(`${FACT_CONTROL_TERMS_PATH}/add/${conditionId}`, "post"),
);
//patch
export const updateTermsForCondition = createAsyncThunk(
  "factories/updateTermsForCondition",
  async ({conditionId, data}) => fetchList(`${FACT_CONTROL_TERMS_PATH}/update/${conditionId}`, "patch", data),
);
//delete
export const deleteControlTerms = createAsyncThunk("factories/deleteControlTerms", async conditionId =>
  fetchList(`${FACT_CONTROL_TERMS_PATH}/delete/${conditionId}`, "delete"),
);
